/**
 * NavIcons.tsx
 * Premium circular SVG icons for the sidebar navigation.
 * Each icon uses a gradient circle + white shape — matching the home icon style.
 */
import React from 'react';

interface IconProps {
  size?: number;
  active?: boolean;
}

/* ── Home / Dashboard ──────────────────────────────────────────────────────── */
export const HomeIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="homeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#b2f0a0" />
        <stop offset="100%" stopColor="#3dbf3d" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#homeGrad)" />
    {/* House shape */}
    <path
      d="M14 7.5L7.5 13.2V20.5H11.5V16H16.5V20.5H20.5V13.2L14 7.5Z"
      fill="white"
    />
  </svg>
);

/* ── Bookings / Calendar+Clock (circular sidebar icon) ──────────────── */
export const BookingsIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bookGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#a0c8f8" />
        <stop offset="100%" stopColor="#3a7bd5" />
      </linearGradient>
    </defs>
    {/* Circle background */}
    <circle cx="14" cy="14" r="14" fill="url(#bookGrad)" />
    {/* Calendar body */}
    <rect x="7" y="9.5" width="12" height="11" rx="1.5" fill="none" stroke="white" strokeWidth="1.2" />
    {/* Header fill */}
    <rect x="7" y="9.5" width="12" height="3" rx="1.2" fill="white" opacity="0.3" />
    {/* Ring tabs */}
    <rect x="10" y="7.5" width="1.8" height="3" rx="0.9" fill="white" />
    <rect x="16" y="7.5" width="1.8" height="3" rx="0.9" fill="white" />
    {/* Grid row 1 */}
    <rect x="9"  y="14" width="2.5" height="2" rx="0.4" fill="white" />
    <rect x="12.5" y="14" width="2.5" height="2" rx="0.4" fill="white" />
    {/* Grid row 2 */}
    <rect x="9"  y="17" width="2.5" height="2" rx="0.4" fill="white" />
    {/* Clock badge */}
    <circle cx="20" cy="20" r="5.5" fill="#4da6f5" />
    <circle cx="20" cy="20" r="3.8" fill="none" stroke="white" strokeWidth="0.8" />
    {/* Hour hand */}
    <line x1="20" y1="20" x2="20" y2="17.5" stroke="white" strokeWidth="1.1" strokeLinecap="round" />
    {/* Minute hand */}
    <line x1="20" y1="20" x2="22" y2="21.2" stroke="white" strokeWidth="1.1" strokeLinecap="round" />
    <circle cx="20" cy="20" r="0.6" fill="white" />
  </svg>
);

/* ── Notifications / Bell ──────────────────────────────────────────────────── */
export const NotificationsIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="notifGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd580" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#notifGrad)" />
    {/* Bell */}
    <path
      d="M14 8C11.24 8 9 10.24 9 13V18H19V13C19 10.24 16.76 8 14 8Z"
      fill="white"
    />
    <rect x="12.5" y="18" width="3" height="1.5" rx="0.5" fill="white" />
    <circle cx="14" cy="8" r="1" fill="white" />
    <path d="M12.5 19.5C12.5 20.33 13.17 21 14 21C14.83 21 15.5 20.33 15.5 19.5" stroke="white" strokeWidth="1" fill="none" />
  </svg>
);

/* ── Employees / People ────────────────────────────────────────────────────── */
export const EmployeesIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="empGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c4b5fd" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#empGrad)" />
    <circle cx="11" cy="12" r="2.5" fill="white" />
    <path d="M6 20C6 17.24 8.24 15 11 15C11.7 15 12.36 15.16 12.96 15.44" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <circle cx="17" cy="12" r="2.5" fill="white" opacity="0.7" />
    <path d="M15.04 15.44C15.64 15.16 16.3 15 17 15C19.76 15 22 17.24 22 20" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7" />
  </svg>
);

/* ── Customers ─────────────────────────────────────────────────────────────── */
export const CustomersIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="custGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f9a8d4" />
        <stop offset="100%" stopColor="#ec4899" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#custGrad)" />
    <circle cx="14" cy="11.5" r="3" fill="white" />
    <path d="M8 21C8 17.69 10.69 15 14 15C17.31 15 20 17.69 20 21" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
  </svg>
);

/* ── Settings / Gear ───────────────────────────────────────────────────────── */
export const SettingsIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="settGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#94a3b8" />
        <stop offset="100%" stopColor="#475569" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#settGrad)" />
    <circle cx="14" cy="14" r="2.5" fill="white" />
    <path
      d="M14 8.5V10M14 18V19.5M8.5 14H10M18 14H19.5M9.9 9.9L10.96 10.96M17.04 17.04L18.1 18.1M9.9 18.1L10.96 17.04M17.04 10.96L18.1 9.9"
      stroke="white" strokeWidth="1.5" strokeLinecap="round"
    />
  </svg>
);

/* ── Reviews / Star ────────────────────────────────────────────────────────── */
export const ReviewsIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="revGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#revGrad)" />
    <path
      d="M14 8l1.8 3.6 4 .58-2.9 2.83.68 3.99L14 17.1l-3.58 1.88.68-3.99L8.2 12.18l4-.58L14 8z"
      fill="white"
    />
  </svg>
);

/* ── Earnings / Money ──────────────────────────────────────────────────────── */
export const EarningsIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="earnGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6ee7b7" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#earnGrad)" />
    <text x="14" y="18.5" textAnchor="middle" fontSize="12" fontWeight="bold" fill="white">$</text>
  </svg>
);

/* ── Profile / Person ──────────────────────────────────────────────────────── */
export const ProfileIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="profGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#93c5fd" />
        <stop offset="100%" stopColor="#2563eb" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#profGrad)" />
    <circle cx="14" cy="12" r="3" fill="white" />
    <path d="M8 22C8 18.69 10.69 16 14 16C17.31 16 20 18.69 20 22" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
  </svg>
);

/* ── About / Info ──────────────────────────────────────────────────────────── */
export const AboutIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="aboutGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#a5f3fc" />
        <stop offset="100%" stopColor="#0891b2" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#aboutGrad)" />
    <text x="14" y="19" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white">i</text>
  </svg>
);

/* ── Admin / Lock ──────────────────────────────────────────────────────────── */
export const AdminIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="adminGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fca5a5" />
        <stop offset="100%" stopColor="#dc2626" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#adminGrad)" />
    <rect x="10" y="13" width="8" height="7" rx="1.5" fill="white" />
    <path d="M11.5 13V11.5C11.5 9.57 13.07 8 15 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    <circle cx="14" cy="16.5" r="1" fill="#dc2626" />
  </svg>
);

/* ── Messages / Chat ───────────────────────────────────────────────────────── */
export const MessagesIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="msgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#a5b4fc" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#msgGrad)" />
    <path d="M8 9h12a1 1 0 011 1v8a1 1 0 01-1 1H9l-3 2V10a1 1 0 011-1z" fill="white" />
    <rect x="11" y="13" width="6" height="1" rx="0.5" fill="#6366f1" />
    <rect x="11" y="11" width="6" height="1" rx="0.5" fill="#6366f1" />
  </svg>
);

/* ── Debug / Wrench ────────────────────────────────────────────────────────── */
export const DebugIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="dbgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d1d5db" />
        <stop offset="100%" stopColor="#6b7280" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#dbgGrad)" />
    <path d="M17.5 10.5C16.7 9.7 15.4 9.7 14.6 10.5L10.5 14.6C9.7 15.4 9.7 16.7 10.5 17.5C11.3 18.3 12.6 18.3 13.4 17.5L17.5 13.4C18.3 12.6 18.3 11.3 17.5 10.5Z" fill="white" />
  </svg>
);

/* ── Business Requests ─────────────────────────────────────────────────────── */
export const RequestsIcon: React.FC<IconProps> = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="reqGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fdba74" />
        <stop offset="100%" stopColor="#ea580c" />
      </linearGradient>
    </defs>
    <circle cx="14" cy="14" r="14" fill="url(#reqGrad)" />
    <rect x="9" y="8" width="10" height="13" rx="1.5" fill="white" opacity="0.9" />
    <path d="M12 12h4M12 14.5h4M12 17h2" stroke="#ea580c" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

/**
 * CalendarClockLargeIcon
 * Standalone hero icon — dark navy calendar 3x3 grid + blue clock badge.
 * Transparent background. Use as a page illustration.
 */
export const CalendarClockLargeIcon: React.FC<{ size?: number }> = ({ size = 96 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="14" width="70" height="72" rx="8" fill="white" stroke="#2d3654" strokeWidth="4.5" />
    <rect x="4" y="14" width="70" height="18" rx="8" fill="#2d3654" />
    <rect x="4" y="25" width="70" height="7" fill="#2d3654" />
    <rect x="20" y="7" width="10" height="16" rx="5" fill="#2d3654" />
    <rect x="48" y="7" width="10" height="16" rx="5" fill="#2d3654" />
    <rect x="10" y="38" width="16" height="13" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="31" y="38" width="16" height="13" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="52" y="38" width="16" height="13" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="10" y="56" width="16" height="13" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="31" y="56" width="16" height="13" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="52" y="56" width="16" height="13" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="10" y="74" width="16" height="11" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <rect x="31" y="74" width="16" height="11" rx="4" fill="none" stroke="#2d3654" strokeWidth="3.5" />
    <circle cx="76" cy="78" r="22" fill="#4da6f5" />
    <circle cx="76" cy="78" r="15" fill="none" stroke="white" strokeWidth="2" />
    <line x1="76" y1="78" x2="70" y2="70" stroke="white" strokeWidth="3" strokeLinecap="round" />
    <line x1="76" y1="78" x2="84" y2="83" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="76" cy="78" r="2.5" fill="white" />
  </svg>
);
